import { Link, Stack, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import {
  Card,
  Checkbox,
  Divider,
  SectionMessage,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { useFormikContext } from "formik";
import { t } from "i18next";
import { useState } from "react";
import { VisuallyHidden } from "react-aria-components";

import { VerificationTemplate } from "../create/VerificationTemplatesContext";

import { FCARegulatedReferencesLearnMoreModal } from "./FCARegulatedReferencesLearnMoreModal";
import { useShouldShowRegulatoryReferences } from "../hooks";

type FcaFormValues = VerificationTemplate & Record<string, unknown>;

const fcaGuidanceAlertText = css({
  fontWeight: 600,
});

export interface FCARegulatedReferencesProps {
  isReadOnly: boolean;
  showYearsGuidance?: boolean;
}

export function FCARegulatedReferences({
  isReadOnly,
  showYearsGuidance = true,
}: FCARegulatedReferencesProps) {
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const { values: rawValues, setValues } = useFormikContext();
  const values = rawValues as FcaFormValues;
  const fcaEnabled =
    values.check_arguments.fca_regulated_references_enabled === true;
  const shouldShowRegulatoryReferences = useShouldShowRegulatoryReferences();

  if (!shouldShowRegulatoryReferences) {
    return null;
  }

  const handleFcaChange = (selected: boolean) => {
    setValues({
      ...values,
      check_arguments: {
        ...values.check_arguments,
        fca_regulated_references_enabled: selected,
        fca_firm_reference_number: selected
          ? values.check_arguments.fca_firm_reference_number ?? ""
          : "",
      },
    });
  };

  const frnDisabled = isReadOnly || !fcaEnabled;

  return (
    <Stack gap="space-4">
      <Text style="form/section-heading">{t("Regulatory references")}</Text>
      <Card border="border-base" padding="space-4" testID="fca-regulatory-card">
        <Stack gap="space-4">
          <Stack
            orientation="horizontal"
            justify="space-between"
            align="start"
            gap="space-4"
          >
            <Stack gap="space-1">
              <Text style="input/label">
                {t("Include FCA regulated references")}
              </Text>
              <Text style="input/helper" color="text-weak">
                {t(
                  "Use this if you are hiring for FCA-regulated roles in the UK."
                )}{" "}
                <Link
                  onPress={() => setLearnMoreOpen(true)}
                  testID="fca-regulatory-learn-more"
                  linkStyle="monochrome"
                >
                  {t("Learn more")}
                </Link>
              </Text>
            </Stack>
            <div style={{ flexShrink: 0 }}>
              <Checkbox
                testID="fca-regulated-references"
                isSelected={fcaEnabled}
                isDisabled={isReadOnly}
                onChange={handleFcaChange}
              >
                <VisuallyHidden>
                  {t("Include FCA regulated references")}
                </VisuallyHidden>
              </Checkbox>
            </div>
          </Stack>

          <Divider />

          <TextInputFormikField
            name="check_arguments.fca_firm_reference_number"
            label={t("FCA Firm Reference Number (FRN)")}
            isRequired={fcaEnabled && !isReadOnly}
            isDisabled={frnDisabled}
            testID="fca-firm-reference-number"
            description={t(
              "Enter the 6–7 digit number issued by the Financial Conduct Authority."
            )}
            inputMode="numeric"
            autoComplete="off"
            pattern="[0-9]*"
          />
        </Stack>
      </Card>

      {showYearsGuidance && (
        <SectionMessage
          type="info"
          icon="info"
          testID="fca-regulatory-guidance"
        >
          <span className={fcaGuidanceAlertText}>
            {t(
              "FCA guidance typically requires references covering the past 6 years. Ensure your configuration meets your compliance requirements."
            )}
          </span>
        </SectionMessage>
      )}
      <FCARegulatedReferencesLearnMoreModal
        isOpen={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
      />
    </Stack>
  );
}
